import React from 'react';
import SEO from '../components/SEO';
import { Shield, Clock, PoundSterling, CheckCircle, ArrowRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Frameworks() {
  return (
    <>
      <SEO
        title="Frameworks & Procurement | UK Public Sector AI Automation"
        description="Access our services through G-Cloud 14, DOS7, and other UK government frameworks. Direct awards up to £10M with 2-4 week procurement timelines."
        keywords={[
          'G-Cloud 14',
          'Digital Marketplace',
          'Government Frameworks',
          'Public Sector Procurement',
          'DOS7',
          'Framework Agreements',
          'Direct Award',
          'UK Government Contracts'
        ]}
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
              <Shield className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 text-sm font-medium">Approved Framework Supplier</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Frameworks & Procurement
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Multiple routes to procure our AI automation services. Choose the pathway that best suits your requirements and contract value.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-blue-500/30 rounded-xl p-8">
              <div className="bg-blue-500/20 p-4 rounded-lg inline-flex mb-6">
                <Shield className="w-10 h-10 text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">G-Cloud 14</h3>
              <p className="text-gray-400 mb-6">Lot 2: Cloud Software (SaaS)</p>

              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Direct Award Up To</p>
                  <p className="text-2xl font-bold text-white">£10M</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Typical Timeline</p>
                  <p className="text-xl font-semibold text-white">2-4 weeks</p>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>Fastest procurement route</span>
                </li>
                <li className="flex items-start space-x-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>No competitive tender required</span>
                </li>
                <li className="flex items-start space-x-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>Pre-approved pricing and T&Cs</span>
                </li>
              </ul>

              <a
                href="https://www.digitalmarketplace.service.gov.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                View on Digital Marketplace
                <ExternalLink className="w-4 h-4 ml-2" />
              </a>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm border border-teal-500/30 rounded-xl p-8">
              <div className="bg-teal-500/20 p-4 rounded-lg inline-flex mb-6">
                <Clock className="w-10 h-10 text-teal-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Further Competition</h3>
              <p className="text-gray-400 mb-6">For contracts over £10M</p>

              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Contract Value</p>
                  <p className="text-2xl font-bold text-white">£10M+</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Typical Timeline</p>
                  <p className="text-xl font-semibold text-white">4-8 weeks</p>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span>Competitive framework tender</span>
                </li>
                <li className="flex items-start space-x-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span>Custom requirements and pricing</span>
                </li>
                <li className="flex items-start space-x-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span>Large-scale implementations</span>
                </li>
              </ul>

              <Link
                to="/contact"
                className="w-full inline-flex items-center justify-center px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors"
              >
                Discuss Requirements
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm border border-green-500/30 rounded-xl p-8">
              <div className="bg-green-500/20 p-4 rounded-lg inline-flex mb-6">
                <PoundSterling className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Below Threshold</h3>
              <p className="text-gray-400 mb-6">Direct procurement route</p>

              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Contract Value</p>
                  <p className="text-2xl font-bold text-white">Under £12k</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Typical Timeline</p>
                  <p className="text-xl font-semibold text-white">1-2 weeks</p>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Immediate start possible</span>
                </li>
                <li className="flex items-start space-x-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Ideal for pilots and POCs</span>
                </li>
                <li className="flex items-start space-x-2 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Minimal procurement overhead</span>
                </li>
              </ul>

              <Link
                to="/contact"
                className="w-full inline-flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
              >
                Start Pilot Project
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-12 mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Our 5-Step Process</h2>
            <div className="grid md:grid-cols-5 gap-8">
              {[
                { step: '1', title: 'Discovery Call', desc: '30 mins, Free', time: 'Day 1' },
                { step: '2', title: 'Free Assessment', desc: 'Process analysis, ROI calculation', time: 'Week 1' },
                { step: '3', title: 'Pilot/POC', desc: 'Prove value', time: '2-4 weeks' },
                { step: '4', title: 'Full Deployment', desc: 'Phased rollout', time: '4-12 weeks' },
                { step: '5', title: 'Continuous Improvement', desc: 'Quarterly reviews', time: 'Ongoing' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="bg-teal-500/20 text-teal-400 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-400 mb-1">{item.desc}</p>
                  <p className="text-xs text-gray-500">{item.time}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-gray-800/80 to-gray-900/80 backdrop-blur-sm border border-gray-700/50 rounded-xl p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Start?</h2>
            <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
              Book a free 30-minute discovery call to discuss your requirements and determine the best procurement route for your needs.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white text-lg font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl"
            >
              Book Discovery Call
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
